import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, Typography, RadioGroup, FormControlLabel, Radio, TextField, InputAdornment, Box, useTheme, useMediaQuery } from '@mui/material';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { useForm } from 'react-hook-form';
import login from 'services/Login/LoginService';
import { warningNotification } from 'utils/toastify';
import { db } from 'db/db-dexie';
import Swal from 'sweetalert2';

const RoleSwitcher = ({ usuario, rolTipo, nombreSucursal, ambiente }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { register } = useForm();
    const [listaRol, setListaRol] = useState([]);
    const [idRol, setIdRol] = useState();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setListaRol(JSON.parse(localStorage.getItem('roles')) || []);
        setIdRol(Number(localStorage.getItem('idRol')));
    }, []);

    const handleClickOpen = () => {
        if (listaRol.length > 1) {
            setOpen(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSearch('');
    };

    const handleToggle = async (e) => {
        const value = Number(e.target.value);
        if (value === idRol) return;

        const rolActual = JSON.parse(localStorage.getItem("nombreRolTipo"));
        const sucursalActual = JSON.parse(localStorage.getItem("nombreSucursal"));
        const nuevoRolObj = listaRol.find((r) => r.idRol === value);

        if (!nuevoRolObj) return;

        // Cierra el dialog modal para que SweetAlert se vea por encima claramente
        handleClose();

        Swal.fire({
            title: '¿Deseas cambiar de rol?',
            html: `Vas a cambiar de:<br>
            <b>${rolActual} - ${sucursalActual}</b><br><br>
            a:<br>
            <b>${nuevoRolObj.descripcion} - ${nuevoRolObj.sucursal}</b><br><br>
            Al cambiar de rol, el sistema actualizará tus permisos, menús y sucursal activa.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#6fbf73',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, cambiar rol',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                await db.account.clear();
                await db.subAccount.clear();
                await db.functions.clear();

                localStorage.setItem('go', true);
                localStorage.removeItem("PeriodoSeleccionado");

                login.GetChange({ id: value })
                    .then(async (resultLogin) => {
                        if (resultLogin.code === '1') {
                            const resp = resultLogin.payload;
                            localStorage.setItem('roles', JSON.stringify(resp.roles));
                            localStorage.setItem('go', false);
                            localStorage.setItem('JWT', JSON.stringify('Bearer ' + resp.token));
                            localStorage.setItem('codigoUsuario', JSON.stringify(resp.codigoUsuario));
                            localStorage.setItem('user', JSON.stringify(resp.user));
                            localStorage.setItem('idSucursal', JSON.stringify(resp.idSucursal));
                            localStorage.setItem('nombreSucursal', JSON.stringify(resp.nombreSucursal));
                            localStorage.setItem('nombreRolTipo', JSON.stringify(resp.nombreRol));
                            localStorage.setItem('codigoRolTipo', JSON.stringify(resp.codigoRolTipo));
                            localStorage.setItem('ambiente', JSON.stringify(resp.ambiente));
                            localStorage.setItem('idRol', JSON.stringify(resp.idRol));
                            localStorage.setItem('idUsuario', JSON.stringify(resp.idUsuario));
                            localStorage.setItem('hour', JSON.stringify(3));
                            localStorage.setItem('minute', JSON.stringify(54));
                            localStorage.setItem('second', JSON.stringify(59));
                            localStorage.setItem('config', false);
                            localStorage.removeItem('cachedSucursalId');

                            // Limpiar datos contables de la sucursal anterior inmediatamente
                            try {
                                await db.account.clear();
                                await db.subAccount.clear();
                                await db.functions.clear();
                            } catch (e) {
                                console.warn('Error limpiando IndexedDB al cambiar rol:', e);
                            }

                            Swal.fire({
                                title: '¡Completado!',
                                text: `Rol actualizado correctamente. Ahora estás trabajando como ${resp.nombreRol} en ${resp.nombreSucursal}.`,
                                icon: 'success',
                                confirmButtonColor: '#6fbf73'
                            }).then(() => {
                                startSesion(resultLogin.redirectTo, login.verifyLogin());
                            });
                        } else {
                            warningNotification(resultLogin.message);
                        }
                    })
                    .catch((error) => {
                        warningNotification("No se pudo cambiar el rol. Intenta nuevamente.");
                    });
            } else {
                setOpen(true);
            }
        });
    };

    const startSesion = (ruta, valor) => {
        if (!valor) {
            // Avisar a React que el rol cambió para que resetee el MainLayout (key={roleId})
            window.dispatchEvent(new Event('roleChanged'));
            // Usamos navigation para cambiar la ruta si es distinta, sin forzar reload violento
            window.location.href = ruta;
        }
    };

    const requestSearch = (e) => {
        setSearch(e.target.value);
    };

    const results = !search ? listaRol : listaRol.filter((dato) => dato.sucursal.toLowerCase().includes(search.toLocaleLowerCase()));

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" color="textSecondary">
                    Bienvenido(a): <strong>{usuario}</strong> | Ambiente: <strong>{ambiente}</strong>
                </Typography>
            </Box>

            <Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SupervisedUserCircleIcon sx={{ fontSize: '18px !important' }} />}
                    endIcon={listaRol.length > 1 ? <KeyboardArrowDownIcon sx={{ fontSize: '18px !important' }} /> : null}
                    onClick={handleClickOpen}
                    disableElevation
                    sx={{
                        textTransform: 'none',
                        borderRadius: '20px',
                        backgroundColor: listaRol.length > 1 ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
                        borderColor: listaRol.length > 1 ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                        color: theme.palette.text.primary,
                        boxShadow: 'none',
                        padding: isMobile ? '2px 8px' : '4px 12px',
                        minWidth: 'auto',
                        height: '34px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                            backgroundColor: listaRol.length > 1 ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                            borderColor: listaRol.length > 1 ? 'rgba(33, 150, 243, 0.5)' : 'transparent',
                        }
                    }}
                    disableRipple={listaRol.length <= 1}
                >
                    {isMobile ? (
                        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem' }}>
                            {rolTipo}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', display: { xs: 'none', md: 'block' } }}>
                                Rol actual |
                            </Typography>
                            <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem' }}>
                                {rolTipo} &middot; {nombreSucursal}
                            </Typography>
                        </Box>
                    )}
                </Button>
            </Box>

            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle>
                    <Typography variant="h4" component="span" fontWeight="600">Cambiar de Rol</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {listaRol.length > 1 && (
                        <TextField
                            {...register('sucursal')}
                            onChange={requestSearch}
                            fullWidth
                            size="small"
                            placeholder="Buscar sucursal o rol..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    )}
                    <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
                        <RadioGroup value={idRol} onChange={handleToggle}>
                            {results.map((row) => {
                                const isCurrent = row.idRol === idRol;
                                return (
                                    <ListItemButton
                                        key={row.idRol}
                                        disableGutters
                                        sx={{
                                            py: 1,
                                            px: 1.5,
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            backgroundColor: isCurrent ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isCurrent ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value={row.idRol}
                                            control={<Radio size="small" color="primary" />}
                                            label={
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight={isCurrent ? "600" : "500"} color={isCurrent ? "primary.main" : "textPrimary"}>
                                                            {row.descripcion}
                                                        </Typography>
                                                        {isCurrent && (
                                                            <Typography variant="caption" sx={{ backgroundColor: 'primary.light', color: 'primary.main', px: 1, py: 0.2, borderRadius: '10px', fontSize: '0.65rem', fontWeight: 'bold' }}>
                                                                Actual
                                                            </Typography>
                                                        )}
                                                        {row.inicial === 1 && (
                                                            <Typography variant="caption" sx={{ backgroundColor: '#e0e0e0', color: '#616161', px: 1, py: 0.2, borderRadius: '10px', fontSize: '0.65rem' }}>
                                                                Predeterminado
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {row.sucursal}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </RadioGroup>
                    </List>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} color="inherit" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: '600' }}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoleSwitcher;
